/**
 * Mirrors `MessageTemplate.LOCKED_TYPES` in blytzpay-django. The API refuses to
 * delete or deactivate these (crm/views.py destroy, crm/serializers.py
 * validate), because the platform fires them off events — a declined card, a
 * promise made, autopay switched on — not a merchant pressing send.
 */
export const SYSTEM_TEMPLATE_TYPES = Object.freeze([
  'AUTOPAY',
  'AUTOPAY_DISABLED',
  'AUTOPAY_DISABLED_MERCHANT',
  'AUTOPAY_MERCHANT',
  'PAYMENT_FAILED',
  'PAYMENT_PROMISE',
  'PAYMENT_PROMISE_CONFIRMED',
  'PAYMENT_SCHEDULED',
  'PAYMENTREQUEST',
  'THANKS',
  'WELCOME',
  'REGISTER',
  'UNCONFIRMED_PROMISE',
  'UNCONFIRMED_PAYMENT_METHOD',
  'DO_NOT_HONOR',
  'INSUFFICIENT_FUNDS',
  'INSUFFICIENT_FUNDS_NO_MULTI',
  'EXCEEDS_WITHDRAWAL_LIMIT',
  'BLOCKED_FIRST_USED',
  'INVALID_CARD_NUMBER',
  'ACCOUNT_CLOSED',
  'EXPIRED_CARD',
  'CVV2_MISMATCH',
  'DECLINED_CONTACT_ISSUER',
  'PICK_UP_CARD',
  'STOP_RECURRING',
  'GENERAL_ERROR',
  'ISSUER_UNAVAILABLE',
  'INVALID_TRANSACTION'
])

export function isSystemTemplateType(type: string | null | undefined): boolean {
  return !!type && SYSTEM_TEMPLATE_TYPES.includes(type)
}

/**
 * `delta_days` on a message template encodes both distance and direction against the invoice due date:
 * positive sends before it, negative after, zero on the day, null not at all. The sign convention is
 * set on save in UpdateMessageTemplateView (`delta_days * dayInRelation`) and consumed by the crm send
 * task, so anything rendering it read-only decodes it here rather than re-deriving the rule.
 */
export function formatTemplateAutomation(
  deltaDays: number | null | undefined,
  type?: string | null
): string {
  if (deltaDays == null) {
    // Every system template is seeded with a null delta, so "Manual" would
    // read as merchant-driven when an event is what actually sends it.
    return isSystemTemplateType(type) ? 'System' : 'Manual'
  }

  if (deltaDays === 0) {
    return 'On due date'
  }

  const days = Math.abs(deltaDays)
  const unit = days === 1 ? 'day' : 'days'
  const direction = deltaDays > 0 ? 'before' : 'after'

  return `${days} ${unit} ${direction} due`
}

// Ranks for the two unscheduled groups.
export const AUTOMATION_RANK_MANUAL = Number.MAX_SAFE_INTEGER - 1
export const AUTOMATION_RANK_SYSTEM = Number.MAX_SAFE_INTEGER

/**
 * Sorting on the rendered label would order "10 days before" ahead of "3 days before". Sort on send
 * order instead — earliest first — then Manual, then System. `type` is required to separate the two
 * unscheduled groups: every System template carries a null delta, exactly like a Manual one, so keying
 * on deltaDays alone ranks them equal and a stable sort leaves them interleaved in whatever order the
 * API returned (bug report).
 */
export function templateAutomationSortKey(
  deltaDays: number | null | undefined,
  type?: string | null,
  groupCount = 0
): number {
  if (groupCount > 0) return AUTOMATION_RANK_GROUPS
  if (deltaDays == null) {
    return isSystemTemplateType(type)
      ? AUTOMATION_RANK_SYSTEM
      : AUTOMATION_RANK_MANUAL
  }

  return -deltaDays
}

/** A customer group that sends a template: core's group pkid and its name. */
export interface TemplateGroup {
  pkid: number
  name: string
}

interface AutomationSortable {
  name?: string | null
  delta_days?: number | null
  type?: string | null
  /**
   * the customer groups that send this template on a schedule.
   */
  groups?: TemplateGroup[]
}

export const AUTOMATION_RANK_GROUPS = -Number.MAX_SAFE_INTEGER

/** The three kinds of template the Automation column can describe. */
export const AUTOMATION_GROUP = Object.freeze({
  ALL: 'ALL',
  AUTOMATED: 'AUTOMATED',
  MANUAL: 'MANUAL',
  SYSTEM: 'SYSTEM'
})

/** Filter options for the list view, in the same order the sort groups them. */
export const AUTOMATION_FILTERS = Object.freeze([
  { value: AUTOMATION_GROUP.ALL, label: 'All' },
  { value: AUTOMATION_GROUP.AUTOMATED, label: 'Automated' },
  { value: AUTOMATION_GROUP.MANUAL, label: 'Manual' },
  { value: AUTOMATION_GROUP.SYSTEM, label: 'System' }
])

/**
 * Which group a template belongs to — the same three-way split the Automation
 * cell renders, so a row labelled "System" is always in the System filter and a
 * row showing a schedule is always in Automated.
 */
export function templateAutomationGroup(
  deltaDays: number | null | undefined,
  type?: string | null,
  groupCount = 0
): string {
  if (groupCount > 0 || deltaDays != null) return AUTOMATION_GROUP.AUTOMATED

  return isSystemTemplateType(type)
    ? AUTOMATION_GROUP.SYSTEM
    : AUTOMATION_GROUP.MANUAL
}

/** True when the template belongs in the selected filter; ALL matches everything. */
export function matchesAutomationFilter(
  template: AutomationSortable,
  filter: string
): boolean {
  if (!filter || filter === AUTOMATION_GROUP.ALL) return true

  return (
    templateAutomationGroup(
      template.delta_days,
      template.type,
      template.groups?.length ?? 0
    ) === filter
  )
}

/**
 * Ascending comparator for the Automation column. Groups first (scheduled by send order, then Manual,
 * then System), then name within a group. The name tiebreak matters because every template in a group
 * shares one rank — all System rows, all Manual rows, any two templates on the same schedule — and a
 * stable sort would leave those ties in API order, which reads as unsorted. Negate the result for
 * descending.
 */
export function compareTemplatesByAutomation(
  a: AutomationSortable,
  b: AutomationSortable
): number {
  const rank =
    templateAutomationSortKey(a.delta_days, a.type, a.groups?.length ?? 0) -
    templateAutomationSortKey(b.delta_days, b.type, b.groups?.length ?? 0)

  if (rank !== 0) return rank

  return String(a.name ?? '').localeCompare(String(b.name ?? ''))
}
